import { useState } from 'react';
import { Phone, MapPin, Mail, Clock, Shield, CheckCircle, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import { SEO } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  { value: 'executive-protection', label: 'Executive Protection' },
  { value: 'k9-security', label: 'K9 Property Guarding' },
  { value: 'drone-surveillance', label: 'Drone Surveillance' },
  { value: 'armed-security', label: 'Armed Security' },
  { value: 'unarmed-security', label: 'Unarmed Security' },
  { value: 'patrol-services', label: 'Security Patrols' },
  { value: 'cctv-monitoring', label: 'CCTV Installation & Monitoring' },
  { value: 'other', label: 'Other / General Inquiry' },
];

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We will contact you shortly.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_type: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="contact-page">
      <SEO 
        title="Contact Us" 
        description="Contact Surfside Patrol for a free security consultation. Call 714-588-2460 or visit us at 2102 Business Center Dr, Irvine, CA 92612. 24/7 dispatch available."
      />
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#121212]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4">
              Contact Us
            </p>
            <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tighter text-white mb-6 font-['Barlow_Condensed']">
              Get In<br />
              <span className="text-[#F5C518]">Touch</span>
            </h1>
            <p className="text-lg text-zinc-400 leading-relaxed">
              Ready to discuss your security needs? Contact us for a free consultation 
              and customized security assessment. Our team is available 24/7 to respond 
              to your inquiries.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-[#121212] border border-zinc-800 p-8" data-testid="contact-form-container">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
                Request a Quote
              </h2>
              
              {isSubmitted ? (
                <div className="text-center py-12" data-testid="form-success">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/10 text-green-500 mb-6 rounded-full">
                    <CheckCircle className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
                  <p className="text-zinc-400 mb-6">
                    Your message has been sent successfully. Our team will contact you shortly.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-[#F5C518] hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label className="block text-zinc-400 text-sm uppercase tracking-wider mb-2">
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-[#F5C518] focus:ring-1 focus:ring-[#F5C518] rounded-none h-12"
                      data-testid="input-name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-zinc-400 text-sm uppercase tracking-wider mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-[#F5C518] focus:ring-1 focus:ring-[#F5C518] rounded-none h-12"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm uppercase tracking-wider mb-2">
                        Phone *
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="(714) 555-0000"
                        className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-[#F5C518] focus:ring-1 focus:ring-[#F5C518] rounded-none h-12"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-zinc-400 text-sm uppercase tracking-wider mb-2">
                      Service Needed *
                    </label>
                    <Select value={formData.service_type} onValueChange={handleServiceChange} required>
                      <SelectTrigger 
                        className="bg-zinc-900 border-zinc-800 text-white focus:border-[#F5C518] focus:ring-1 focus:ring-[#F5C518] rounded-none h-12"
                        data-testid="select-service"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {serviceOptions.map(option => (
                          <SelectItem 
                            key={option.value} 
                            value={option.value}
                            className="text-white hover:bg-zinc-800 focus:bg-zinc-800"
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-zinc-400 text-sm uppercase tracking-wider mb-2">
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your security needs..."
                      rows={5}
                      className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-[#F5C518] focus:ring-1 focus:ring-[#F5C518] rounded-none resize-none"
                      data-testid="input-message"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-skew bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-button"
                  >
                    <span className="flex items-center justify-center">
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </span>
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-[#121212] border border-zinc-800 p-8" data-testid="contact-info">
                <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
                  Contact Information
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#F5C518]/10 flex items-center justify-center mr-4">
                      <Phone className="h-5 w-5 text-[#F5C518]" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm uppercase tracking-wider mb-1">Phone</p>
                      <a 
                        href="tel:714-588-2460" 
                        className="text-white text-lg font-bold hover:text-[#F5C518] transition-colors"
                        data-testid="contact-phone"
                      >
                        714-588-2460
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#F5C518]/10 flex items-center justify-center mr-4">
                      <Mail className="h-5 w-5 text-[#F5C518]" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm uppercase tracking-wider mb-1">Email</p>
                      <a 
                        href="mailto:surfside.josh4995@gmail.com" 
                        className="text-white text-lg hover:text-[#F5C518] transition-colors"
                        data-testid="contact-email"
                      >
                        surfside.josh4995@gmail.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#F5C518]/10 flex items-center justify-center mr-4">
                      <MapPin className="h-5 w-5 text-[#F5C518]" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm uppercase tracking-wider mb-1">Address</p>
                      <p className="text-white" data-testid="contact-address">
                        2102 Business Center Dr<br />
                        Irvine, CA 92612
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#F5C518]/10 flex items-center justify-center mr-4">
                      <Clock className="h-5 w-5 text-[#F5C518]" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm uppercase tracking-wider mb-1">Availability</p>
                      <p className="text-white font-bold">24/7 Dispatch Center</p>
                      <p className="text-zinc-400 text-sm">Always available to respond</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* License Info */}
              <div className="bg-[#F5C518]/5 border border-[#F5C518]/20 p-8" data-testid="license-info">
                <div className="flex items-center mb-4">
                  <Shield className="h-8 w-8 text-[#F5C518] mr-3" />
                  <h3 className="text-xl font-bold uppercase text-white font-['Barlow_Condensed']">
                    Licensed & Insured
                  </h3>
                </div>
                <p className="text-zinc-400 mb-4">
                  Surfside Patrol is a fully licensed private security company operating 
                  under California Bureau of Security and Investigative Services.
                </p>
                <p className="text-[#F5C518] font-bold text-2xl">
                  PPO #15982
                </p>
              </div>

              {/* Map */}
              <div className="bg-[#121212] border border-zinc-800 overflow-hidden" data-testid="map-container">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3320.6789!2d-117.8531!3d33.6846!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80dcde9f0e7f3a63%3A0x1234567890abcdef!2s2102%20Business%20Center%20Dr%2C%20Irvine%2C%20CA%2092612!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="250"
                  style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(90%)' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Surfside Patrol Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
